import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { SimpleProjectionYearResult } from '../../../../../types/SimpleProjection';
import { useStockCurrency } from '../../../../../contexts/StockCurrencyContext';
import { getChartColors } from '../../../Charts/constants/chartColors';
import { formatNumber } from '../../../Charts/utils/formatNumber';
import ChartTooltip from '../../../Charts/components/shared/ChartTooltip';
import GenericFinancialBarChart from '../../../Charts/components/shared/GenericFinancialBarChart';

interface ProjectedRevenueChartProps {
  projections: SimpleProjectionYearResult[];
  ticker: string;
  transparentBg?: boolean;
}

const ProjectedRevenueChart: React.FC<ProjectedRevenueChartProps> = ({ projections, ticker, transparentBg = false }) => {
  const theme = useTheme();
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(theme.palette.mode === 'dark');

  const chartData = useMemo(() =>
    projections.map((p) => ({
      period: p.isHistorical ? `${p.yearLabel} (A)` : p.yearLabel,
      date: `${p.yearLabel}-01-01`,
      year: parseInt(p.yearLabel),
      quarter: null,
      revenue: p.revenue / 1e6,
    })),
    [projections]
  );

  return (
    <GenericFinancialBarChart
      title="Projected Revenue"
      ticker={ticker}
      height={350}
      renderCard={!transparentBg}
      isLoading={false}
      error={null}
      chartData={chartData}
      viewMode="annual"
      barKeys={['revenue']}
      colors={(bar: { data: Record<string, number> }) =>
        (bar.data.revenue ?? 0) >= 0 ? colors.positive : colors.negative
      }
      yAxisLegend="Revenue"
      yAxisFormat={(value) => `${value >= 0 ? currencySymbol : `-${currencySymbol}`}${formatNumber(Math.abs(value) * 1000000)}`}
      tooltipContent={({ indexValue, value, color }) => {
        const v = value as number;
        return (
          <ChartTooltip
            header={String(indexValue)}
            headerColor={color}
            rows={[{ label: 'Revenue', value: `${v >= 0 ? currencySymbol : `-${currencySymbol}`}${formatNumber(Math.abs(v) * 1000000, 2)}` }]}
          />
        );
      }}
    />
  );
};

export default ProjectedRevenueChart;
